import { useEffect, useMemo, useState, useRef, useCallback, ChangeEvent } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { InternalPage } from 'theSource/components';
import { FirstDrawerDividerMargin } from './FamilyDetailsPageStyled';
import { chooseFamilyAvatarAction } from 'enrollment/features/ProfileEdit/Avatar/AvatarActions';
import { FamilyDrawerDisplayPicture } from './FamilyDrawerDisplayPicture';
import { AvatarFamilyDrawerDisplayPicture } from './AvatarFamilyDrawerDisplayPicture';
import { FamilyMemberForm } from './components';
import {
  getInitialValues,
  mapFormValues,
  queryClient,
  useApiMutation,
} from 'theSource/utils';
import {
  GET_DEPENDENTS_QUERY_KEY,
  SAVE_NEW_DEPENDENT_QUERY_KEY,
} from 'enrollment/queries/useEnrollmentFamilyQuery';
import { useConfigurationQuery } from 'theSource/queries';
import { ErrorResponseDto, FormValuesDto, ResponseDto } from 'theSource/types';
import { PsButtonTuner } from 'stencilSetup';
import { OrbitDrawer } from 'enrollment/components/Shared/OrbitDrawer';
import { ProfileCropper } from './ProfileCropper';
import { LoaderOverlay } from 'enrollment/components';
import { useToastDialog } from 'enrollment/components/Shared/ToastDialogContext';

interface DrawerProps {
  enrollmentContextType?: string | null;
  openFamilyDetailsDrawer?: boolean;
  handleCloseFamilyDrawer?: () => void;
  setOpenAddFamilyDetailsDrawer: Function,
  categories?: any;
}

export const AddFamilyDetailsPage = ({
  enrollmentContextType,
  openFamilyDetailsDrawer,
  handleCloseFamilyDrawer,
  setOpenAddFamilyDetailsDrawer,
  categories,
}: DrawerProps) => {
  const [openFamilyAvatarDrawer, setOpenFamilyAvatarDrawer] = useState(false);
  const [saveAvatarName, setSaveAvatarName] = useState<string>('');
  const [saveAvatarSkinTone, setSaveAvatarSkinTone] = useState<string>('');
  const [saveAvatarHairColor, setSaveAvatarHairColor] = useState<string>('');
  const [saveAvatarShirtColor, setSaveAvatarShirtColor] = useState<string>('');
  const [saveAvatarValue, setSaveAvatarValue] = useState<string>('');
  const [orientationDivider, setOrientationDivider] = useState<
    'horizontal' | 'vertical'
  >('horizontal');
  const [saveDisabled, setSaveDisabled] = useState<boolean>(true);
  const [familyDisplayPictureSaved, setFamilyDisplayPictureSaved] =
    useState<boolean>(false);
  const [awaitSuccess, setAwaitSuccess] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = useToastDialog();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const setDialogRef = useCallback((el: HTMLDivElement) => {
    containerRef.current = el;
  }, []);

  const familyContainerWidth = document.querySelector(
    '#enrollment-family-container'
  ) as HTMLElement;

  useEffect(() => {
    familyContainerWidth?.offsetWidth <= 945
      ? setOrientationDivider('horizontal')
      : setOrientationDivider('vertical');
  }, [familyContainerWidth]);

  const { t } = useTranslation('pages.enrollmentFamily');

  const dispatch = useDispatch();

  const addDrawerFamilyAvatar = useSelector((state: any) => state?.enrollment);

  const FamilyDisplayPictureIDCondition = '/enrollment/family';

  const avatarObject = addDrawerFamilyAvatar?.chooseAvatar;
  const avatarName = avatarObject?.family_avatar?.name ?? '';

  const avatarSkinTone = avatarObject?.avatarFamilySkinTone?.name ?? '';
  const avatarHairColor = avatarObject?.avatarFamilyHairColor?.name ?? '';
  const avatarShirtColor = avatarObject?.avatarFamilyShirtColor?.name ?? '';
  const croppedFamilyImagePath = avatarObject?.croppedFamilyImagePath ?? '';
  const familyPictureName = avatarObject?.familyPictureName ?? '';
  const chosenFamilyDisplay = avatarObject?.chosenFamilyDisplay ?? '';
  const isFamilyCropped = avatarObject?.isFamilyCropped;

  useEffect(() => {
    !chosenFamilyDisplay && setFamilyDisplayPictureSaved(false);
  }, [chosenFamilyDisplay]);

  const subscriberProfileAvatarValue: string[] = [];
  avatarName && subscriberProfileAvatarValue.push(avatarName);
  avatarSkinTone && subscriberProfileAvatarValue.push(avatarSkinTone);
  avatarHairColor && subscriberProfileAvatarValue.push(avatarHairColor);
  avatarShirtColor && subscriberProfileAvatarValue.push(avatarShirtColor);

  const handleCloseAvatarDrawer = () => {
    setOpenFamilyAvatarDrawer(false);
    dispatch(chooseFamilyAvatarAction({}, FamilyDisplayPictureIDCondition));
  };

  const handleUploadDrawerAvatar = () => {
    setSaveAvatarName(avatarName);
    setSaveAvatarSkinTone(avatarObject?.avatarFamilySkinTone?.colorCode);
    setSaveAvatarHairColor(avatarObject?.avatarFamilyHairColor?.colorCode);
    setSaveAvatarShirtColor(avatarObject?.avatarFamilyShirtColor?.colorCode);
    setSaveAvatarValue(subscriberProfileAvatarValue.join('_'));
    setOpenFamilyAvatarDrawer(false);
    setFamilyDisplayPictureSaved(false);
  };

  const initialValues = useMemo(
    () => getInitialValues(categories),
    [categories]
  );

  const [updatedValues, setUpdatedValues] = useState(initialValues);
  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const removeListenerController = new AbortController();

  const { mutate: createFamilyMember } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    FormValuesDto
  >({
    queryKey: SAVE_NEW_DEPENDENT_QUERY_KEY,
    method: 'post',
    endpoint: `${lumosApiHost}/v2/self_service/dependents/profile/${enrollmentContextType}`,
    onSuccess: () => {
      setAwaitSuccess?.(false);

      document.addEventListener('psSubmitDialog', (e) => {
        setIsSubmitting(false);
        if ((e as CustomEvent).detail === 'addFamilyDetailsDrawer') {
          setAwaitSuccess?.(true);
          setOpenAddFamilyDetailsDrawer(false);

          queryClient.invalidateQueries(
            `${GET_DEPENDENTS_QUERY_KEY}-${enrollmentContextType}`
          );
        }
      });
    },
    onError: (error) => {
      setIsSubmitting(false);
      if (containerRef.current) {
        toast.showToast({
          message: error.response?.data?.details || error.message || 'An unknown error occurred',
          status: 'danger',
          closeIcon: true,
          container: containerRef.current,
        });
      }
    },
    showSuccessSnackbar: false,
    showErrorSnackbar: false,
    toastRef: toast
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    removeListenerController.abort();
    switch (e.target?.type) {
      case 'checkbox':
        setUpdatedValues((prevState: any) => ({
          ...prevState,
          [e.target?.name]: e.target?.checked,
        }));
        break;
      default:
        setUpdatedValues((prevState: any) => ({
          ...prevState,
          [e.target?.name]: value,
        }));
        break;
    }
  };

  const handleSubmit = () => {
    setIsSubmitting(true);

    const newUpdatedValues = mapFormValues(categories ?? [], updatedValues);
    newUpdatedValues['dependent_profile'] = {
      avatar: familyDisplayPictureSaved ? '' : `${saveAvatarValue}`,
      profile_picture: familyDisplayPictureSaved ? croppedFamilyImagePath : '',
      file_name: familyDisplayPictureSaved ? familyPictureName : '',
    };
    createFamilyMember(newUpdatedValues);
  };

  const handleSaveFamilyPictureDrawer = () => {
    setFamilyDisplayPictureSaved(true);
  };

  const renderAvatarBtn = () => {
    return saveAvatarValue === '' || familyDisplayPictureSaved ? (
      <PsButtonTuner
        buttonId="select-avatar-btn"
        label={t('selectAvatarButtonLabel')}
        aria-label={t('selectAvatarButtonLabel')}
        level="tertiary"
        onClick={() => {
          setOpenFamilyAvatarDrawer(true);
        }}
      />
    ) : (
      <PsButtonTuner
        buttonId="edit-avatar-btn"
        label={t('editAvatarButtonLabel')}
        level="tertiary"
        onClick={() => {
          setOpenFamilyAvatarDrawer(true);
        }}
      />
    );
  };

  return (
    <OrbitDrawer
      aria-label="addFamilyDetailsDrawer"
      drawerId="addFamilyDetailsDrawer"
      heading={t('addFamilyMemberTitle')}
      isOpen={openFamilyDetailsDrawer ?? false}
      handleClose={handleCloseFamilyDrawer}
      handleSubmit={handleSubmit}
      awaitSuccess={awaitSuccess}
      footer={true}
      saveButton={t('saveButtonLabel')}
      saveButtonDisabled={saveDisabled}
      dialogRef={setDialogRef}
    >
      <InternalPage testId="add-family-details">
        <Typography variant="body1" textAlign="left">
          {t('addDrawerFamilyDescription')}
        </Typography>
        <FirstDrawerDividerMargin orientation="horizontal" />
        <h2 className="headline-3 text-center mt-3">
          {t('displayPictureTitle')}
        </h2>
        <div className="my-4_5">
          {!familyDisplayPictureSaved && chosenFamilyDisplay !== 'picture' && (
            <FamilyDrawerDisplayPicture
              saveAvatarName={saveAvatarName}
              saveAvatarSkinTone={saveAvatarSkinTone}
              saveAvatarHairColor={saveAvatarHairColor}
              saveAvatarShirtColor={saveAvatarShirtColor}
            />
          )}
        </div>
        <div className="text-center my-4_5">
          <ProfileCropper
            handleSaveFamilyPictureDrawer={handleSaveFamilyPictureDrawer}
            isNewDependent={true}
            familyDisplayPictureSaved={familyDisplayPictureSaved}
          ></ProfileCropper>
        </div>

        <Box sx={{ textAlign: 'center' }}>
          {!(chosenFamilyDisplay === 'picture' && !isFamilyCropped) ? (
            renderAvatarBtn()
          ) : (
            <div />
          )}
        </Box>
        <FirstDrawerDividerMargin orientation="horizontal" />
        <FamilyMemberForm
          categories={categories}
          handleChange={handleChange}
          formType="new"
          controlled={true}
          onFormValidityChange={(isValid) => {
            setSaveDisabled(!isValid);
          }}
          isCropEnabled={chosenFamilyDisplay === 'picture' && !isFamilyCropped}
        />
        <AvatarFamilyDrawerDisplayPicture
          openFamilyAvatarDrawer={openFamilyAvatarDrawer}
          handleCloseAvatarDrawer={handleCloseAvatarDrawer}
          orientationDivider={orientationDivider}
          handleUploadDrawerAvatar={handleUploadDrawerAvatar}
          isNewDependent={true}
        />
      </InternalPage>
      <LoaderOverlay isAbsolute={true} isOverlay={true} isSubmitting={isSubmitting} />
    </OrbitDrawer>
  );
};
